/**
 * Dựng ảnh QR gửi khách: nạp logo → dựng ma trận QR → vẽ mẫu → xuất PNG.
 *
 * Chuỗi VietQR dựng từ đúng số tiền + nội dung của phiếu thu, rồi vẽ vào mẫu
 * "Kem cổ điển" (mẫu duy nhất còn lại từ 30/10/2026).
 */

import { buildVietQRPayload } from "@/components/VietQRGenerator";
import { LOGO_ASSETS, POSTER_HEIGHT, POSTER_WIDTH, type BrandLogo } from "./brand";
import { ensureFontsReady, loadImage } from "./canvas-kit";
import { buildQRMatrix } from "./qr-matrix";
import {
  templateById,
  type PosterContent,
  type QRTemplate,
  type QRTemplateId,
} from "./templates";

export type PosterBankInfo = {
  bankBin: string;
  accountNumber: string;
  accountHolder: string;
};

export type PosterInput = {
  bank: PosterBankInfo;
  content: PosterContent;
  /** Logo dùng ở giữa mã và trên đầu ảnh — suy từ nhóm sản phẩm của đơn. */
  logo: BrandLogo;
};

export type RenderedPoster = {
  id: QRTemplateId;
  label: string;
  hint: string;
  /** Ảnh PNG đã vẽ xong, dạng data URL — dùng để xem trước và để tải về. */
  dataUrl: string;
};

/** Bộ nhớ đệm logo trong phiên — tránh tải lại ảnh mỗi lần mở hộp thoại. */
const imageCache = new Map<string, Promise<HTMLImageElement>>();

function cachedImage(src: string): Promise<HTMLImageElement> {
  const hit = imageCache.get(src);
  if (hit) return hit;
  // Chỉ cache lời hứa THÀNH CÔNG: cache cả lần hỏng thì một lần rớt mạng là
  // ảnh không bao giờ tải lại được cho tới khi tải lại trang.
  const pending = loadImage(src).catch((err: unknown) => {
    imageCache.delete(src);
    throw err;
  });
  imageCache.set(src, pending);
  return pending;
}

function createCanvas(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = POSTER_WIDTH;
  canvas.height = POSTER_HEIGHT;
  return canvas;
}

/**
 * Vẽ một mẫu ra canvas riêng của nó.
 *
 * @throws khi trình duyệt không cho lấy ngữ cảnh 2D (rất hiếm — máy tắt canvas).
 */
function drawTemplate(
  template: QRTemplate,
  input: PosterInput,
  matrix: ReturnType<typeof buildQRMatrix>,
  emblem: HTMLImageElement,
  lockup: HTMLImageElement,
  lockupLight: HTMLImageElement,
): HTMLCanvasElement {
  const canvas = createCanvas();
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Trình duyệt không vẽ được ảnh QR (thiếu canvas 2D).");
  template.draw({ ctx, content: input.content, matrix, emblem, lockup, lockupLight });
  return canvas;
}

/** Vẽ đúng MỘT mẫu — dùng cho màn nhân sự lẫn cổng khách. */
export async function renderPoster(
  input: PosterInput,
  id: QRTemplateId,
): Promise<RenderedPoster> {
  const template = templateById(id);
  const payload = buildVietQRPayload({
    bankBin: input.bank.bankBin,
    accountNumber: input.bank.accountNumber,
    accountHolder: input.bank.accountHolder,
    amount: input.content.amount,
    memo: input.content.memo,
  });
  const matrix = buildQRMatrix(payload);
  const assets = LOGO_ASSETS[input.logo];
  const [emblem, lockup, lockupLight] = await Promise.all([
    cachedImage(assets.emblem),
    cachedImage(assets.lockup),
    cachedImage(assets.lockupLight),
    ensureFontsReady(),
  ]);
  return {
    id: template.id,
    label: template.label,
    hint: template.hint,
    dataUrl: drawTemplate(template, input, matrix, emblem, lockup, lockupLight).toDataURL("image/png"),
  };
}

/** Tên file khi tải về — mã phiếu nằm trong tên để nhân sự khỏi nhầm ảnh. */
export function posterFileName(memo: string, id: QRTemplateId): string {
  const safe = memo.replace(/[^\w-]+/g, "-").replace(/^-+|-+$/g, "") || "thanh-toan";
  return `QR-${safe}-${id}.png`;
}

/** Đổi data URL thành Blob để tải về / chép vào bộ nhớ tạm. */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [head, body] = dataUrl.split(",");
  const mime = /:(.*?);/.exec(head ?? "")?.[1] ?? "image/png";
  const binary = atob(body ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Tải ảnh về máy. */
export function downloadPoster(poster: RenderedPoster, memo: string): void {
  const url = URL.createObjectURL(dataUrlToBlob(poster.dataUrl));
  const link = document.createElement("a");
  link.href = url;
  link.download = posterFileName(memo, poster.id);
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Thu hồi ở lượt sau để Safari kịp bắt đầu tải — thu hồi ngay là file rỗng.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Chép ảnh vào bộ nhớ tạm để dán thẳng vào khung chat.
 *
 * @throws khi trình duyệt không hỗ trợ chép ảnh (Firefox, hoặc trang chạy
 *   HTTP) — người gọi bắt lỗi này để mời nhân sự dùng nút Tải ảnh.
 */
export async function copyPosterToClipboard(poster: RenderedPoster): Promise<void> {
  if (!navigator.clipboard || typeof ClipboardItem === "undefined") {
    throw new Error("Trình duyệt này không chép được ảnh — dùng nút Tải ảnh nhé.");
  }
  const blob = dataUrlToBlob(poster.dataUrl);
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}
